/* eslint-disable max-lines */
/* eslint-disable max-params */
/* eslint-disable import/max-dependencies */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import identity from 'lodash/identity';
import { Checkbox } from '@im/ui-lib/Checkbox';
import { GridRowId, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import { useTranslate } from '@im/react-localization';
import { Tag } from '@im/ui-lib/Tag';
import { LineDetails, ColumnGroups } from '../../../hooks/deal-details';
import RenderHeader from '../../RenderHeader/RenderHeader';
import NumericInput from './NumericInput';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';
import Tooltip from '@mui/material/Tooltip';
import ShareIcon from '@mui/icons-material/Share';
import IconButton from '@mui/material/IconButton';
import SpliteAndRemoveDialog from '../../SplitAndRemove/SplitAndRemoveDialog';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BaseSalesToggle from './BaseSalesToggle';
import { ColumnDef } from './Table';
import { SxProps, Theme } from '@mui/system';
import { getFlattenedLines } from './api';
import {
  ImSkuCol,
  ImSkuTableCol,
  OpenPopupType,
  OrderHistoryCol,
  ProductBusinessTypeCol,
  VpnHistoryCol,
} from './RenderColumnCell';

export function useSortedGroups(columnGroups: ColumnGroups[]) {
  const [sortableList, setSortableList] = useState<string[]>([]);

  const translate = useTranslate();

  const sortedColumnGroups = [...columnGroups]
    .sort((a, b) => sortableList.indexOf(a.name) - sortableList.indexOf(b.name))
    .map(({ name, ...group }) => ({
      ...group,
      name: translate(
        `VendorDeals.DiscountLine${upperFirst(camelCase(name))}Name`
      ),
    }));

  return {
    setSortableList,
    sortableList,
    sortedColumnGroups,
  };
}
export const getCellText = (value: any): string => {
  if (value === null) {
    return 'None';
  }
  if (value === 'True') {
    return 'Exc';
  }
  return 'Inc';
};
export function useSelectedLines(rawLines: LineDetails[]) {
  const [lineQuantities, setLineQuantities] = useState<
    Record<GridRowId, number>
  >({});

  const [allSelected, setAllSelected] = useState(false);

  const [errors, setErrors] = useState<Record<GridRowId, number>>({});

  function setUpLineQuantities(lines: Record<GridRowId, number | undefined>) {
    const consolidatedLines = Object.fromEntries(
      Object.entries(lines)
        .filter(([_, v]) => identity(v))
        .map(([k, v]) => [Number(k), v])
    ) as Record<GridRowId, number>;
    setErrors(
      Object.entries(consolidatedLines).reduce((acc, [id, quantity]) => {
        const min = getLineMinQuantity(id);
        const max = getLineMaxQuantity(id);
        const q = quantity;
        return {
          ...acc,
          ...(q < min && { [id]: 'min' }),
          ...(max && q > max && { [id]: 'max' }),
        };
      }, {})
    );
    return setLineQuantities(consolidatedLines);
  }

  function setUpAllSelected(_value: boolean) {
    const value = !Object.keys(lineQuantities).length && _value;
    setAllSelected(value);
    value
      ? setSelected(
          getFlattenedLines(rawLines)
            .filter((line) => line.productset_type !== 'BC')
            .map((line) => line.item_id),
          true
        )
      : setSelected([]);
  }
  const getLine = (_line: GridRowId | LineDetails) =>
    isNumber(_line) || isString(_line)
      ? getFlattenedLines(rawLines)?.find(
          (line) => line.item_id === Number(_line)
        )
      : _line;

  const getLineMinQuantity = (line: GridRowId | LineDetails) =>
    getLine(line)?.dd_min_qty_limit ?? 1;

  const getLineMaxQuantity = (_line: GridRowId | LineDetails) => {
    const line = getLine(_line);
    if (
      line?.childDiscountLines?.length &&
      line.is_line_split_and_remove_allowed === false &&
      line.dd_available_qty !== 0
    ) {
      return (
        line.childDiscountLines[0].dd_available_qty *
        (line.childDiscountLines[0].dd_max_qty_limit /
          line.dd_vendor_rem_max_qty_limit)
      );
    } else {
      return line?.dd_customer_qty_limit && line.dd_customer_qty_limit > 0
        ? line?.dd_customer_qty_limit
        : line?.dd_available_qty;
    }
  };

  function setSelected(lineIDs: GridRowId[], max?: boolean) {
    lineIDs.length < Object.keys(lineQuantities).length &&
      setAllSelected(false);
    setUpLineQuantities(
      lineIDs.reduce(
        (acc, id) => ({
          ...acc,
          [id]:
            lineQuantities[id] ?? max
              ? getLineMaxQuantity(id)
              : getLineMinQuantity(id),
        }),
        {}
      )
    );
  }

  function toggleLineSelected(lineID: number) {
    setAllSelected(false);
    setUpLineQuantities({
      ...lineQuantities,
      [lineID]: lineQuantities[lineID] ? undefined : getLineMaxQuantity(lineID),
    });
  }

  function setQuantity(lineID: number, quantity: number) {
    setUpLineQuantities({
      ...lineQuantities,
      [lineID]: quantity,
    });
  }

  const selected = Object.keys(lineQuantities).map(Number);

  return {
    allSelected,
    anyLineSelected: !!selected.length,
    errors,
    getLineMaxQuantity,
    getLineMinQuantity,
    lineQuantities,
    selected,
    setQuantity,
    setSelected,
    setUpAllSelected,
    toggleLineSelected,
  };
}

export const useSelectedRows = (rawLines: LineDetails[]) => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [allRowsSelected, setAllRowsSelected] = useState(false);

  const toggleSelectedRows = (lineID: number) => {
    setAllRowsSelected(false);
    const selectedIndex = selectedRows.indexOf(lineID);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, lineID);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1)
      );
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAllRows = (checked: boolean) => {
    if (checked) {
      setAllRowsSelected(true);
      const lines = getFlattenedLines(rawLines).filter(
        (line) => line.productset_type !== 'BC'
      );
      const newSelected = lines?.map((n) => n.item_id);
      setSelectedRows(newSelected);
      return;
    }
    setAllRowsSelected(false);
    setSelectedRows([]);
  };

  return {
    allRowsSelected,
    handleSelectAllRows,
    selectedRows,
    toggleSelectedRows,
  };
};

export function useSelectColumns({
  disableQtyEdit,
  enableSelectAll = true,
  isBidActive,
  isLineIdAllowed,
  isSplitAndRemoveAllowed,
  onError,
  onExpandClick,
  rawLines,
  showCollapsCol,
  showQuantityCol,
}: {
  disableQtyEdit: boolean;
  enableSelectAll: boolean;
  isBidActive: boolean;
  isLineIdAllowed: boolean;
  isSplitAndRemoveAllowed: boolean;
  onError: any;
  onExpandClick: (ids: number[]) => void;
  rawLines: LineDetails[];
  showCollapsCol?: boolean;
  showQuantityCol?: boolean;
}) {
  const translate = useTranslate();
  const {
    allSelected,
    anyLineSelected,
    errors,
    getLineMaxQuantity,
    getLineMinQuantity,
    lineQuantities,
    selected,
    setQuantity,
    setUpAllSelected,
    toggleLineSelected,
  } = useSelectedLines(rawLines);

  const {
    allRowsSelected,
    handleSelectAllRows,
    selectedRows,
    toggleSelectedRows,
  } = useSelectedRows(rawLines);

  const [lineDetailsForSplitAndRemove, setLineDetailsForSplitAndRemove] =
    useState<LineDetails | null>(null);

  const [isExpanded, setIsExpanded] = useState<number[]>([]);
  useEffect(() => {
    onExpandClick(isExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, rawLines]);

  const columns: Partial<
    Record<
      | 'collapsColumn'
      | 'checkboxColumn'
      | 'lineIdColumn'
      | 'spliteAndRemoveColumn'
      | 'quantityColumn',
      ColumnDef<LineDetails>
    >
  > = {
    checkboxColumn: {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      renderCell: ({ row, rowId: id }) =>
        row?.productset_type !== 'BC' && (
          <Checkbox
            {...{
              checked: showQuantityCol
                ? selected.includes(Number(id))
                : selectedRows.includes(Number(id)),
              disabled:
                !isBidActive ||
                (showQuantityCol ? getLineMaxQuantity(row) === 0 : false),
              id: `select-${id}`,
              onChange: () =>
                showQuantityCol
                  ? toggleLineSelected(Number(id))
                  : toggleSelectedRows(Number(id)),
              onClick: (e) => e.stopPropagation(),
            }}
          />
        ),
      renderHeader: () =>
        enableSelectAll || anyLineSelected ? (
          <Checkbox
            {...{
              checked: showQuantityCol ? allSelected : allRowsSelected,
              disabled: !isBidActive,
              id: `select-all`,
              indeterminate: showQuantityCol
                ? anyLineSelected && !allSelected
                : !!selectedRows.length,
              onChange: ({ target: { checked } }: any) =>
                showQuantityCol
                  ? setUpAllSelected(checked)
                  : handleSelectAllRows(checked),
            }}
          />
        ) : (
          <> </>
        ),
      tableCellProps: {
        align: 'center',
        sx: {
          justifyItems: 'center',
          padding: `0px !important`,
        },
        width: 50,
      },
    },
  };

  if (isLineIdAllowed) {
    columns.lineIdColumn = {
      field: 'line_id',
      renderCell: (item) => item.value,
      renderHeader: () => <> </>,
      tableCellProps: {
        width: 100,
      },
    };
  }

  if (showCollapsCol) {
    columns.collapsColumn = {
      field: '__COLLAPSABLE__',
      renderCell: ({ row, rowId: id }) =>
        row?.childDiscountLines &&
        row?.childDiscountLines?.length > 0 && (
          <IconButton
            id={`expand-${id.toString()}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(
                isExpanded.includes(row.item_id)
                  ? isExpanded.filter((e) => e !== row.item_id)
                  : isExpanded.concat(row.item_id)
              );
            }}
            size="small"
            style={{
              ...(isExpanded.includes(row.item_id) && {
                transform: 'rotate(180deg)',
              }),
            }}
          >
            <ExpandLessIcon />
          </IconButton>
        ),
      renderHeader: () => <> </>,
      tableCellProps: {
        align: 'center',
        sx: {
          padding: `0px !important`,
        },
        width: 50,
      },
    };
  }
  if (isSplitAndRemoveAllowed) {
    columns.spliteAndRemoveColumn = {
      field: 'spliteAndRemove',
      renderCell: ({ row }: { row: LineDetails }) => {
        return (
          row.is_line_split_and_remove_allowed && (
            <Tooltip
              onClick={(e) => e.stopPropagation()}
              title={translate('VendorDeals.SplitQuantityOrRemovalOfProducts')}
            >
              <span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setLineDetailsForSplitAndRemove(row);
                  }}
                  size="small"
                >
                  <ShareIcon
                    fontSize="small"
                    sx={{ transform: `rotate(90deg)` }}
                  />
                </IconButton>
                {lineDetailsForSplitAndRemove &&
                  row.dd_tracking ===
                    lineDetailsForSplitAndRemove?.dd_tracking && (
                    <SpliteAndRemoveDialog
                      lineDetails={lineDetailsForSplitAndRemove}
                      onClose={() => setLineDetailsForSplitAndRemove(null)}
                    />
                  )}
              </span>
            </Tooltip>
          )
        );
      },
      renderHeader: () => (
        <RenderHeader
          col={{
            name: '',
          }}
        />
      ),
      tableCellProps: {
        align: 'center',
        sx: {
          padding: `0px !important`,
        },
        width: 50,
      },
    };
  }
  if (showQuantityCol && isBidActive) {
    columns.quantityColumn = {
      field: 'quantity',
      renderCell: ({ row, rowId: id }) => {
        const min = getLineMinQuantity(row);
        const max = getLineMaxQuantity(row);
        return selected.includes(Number(id)) ? (
          <NumericInput
            disableQtyEdit={disableQtyEdit}
            id={String(id)}
            max={max}
            min={min}
            onChange={({ target: { value } }: any) => {
              setQuantity(Number(id), Math.floor(Number(value)));
              if (value < min) {
                onError({ min });
              } else if (max && value > max) {
                onError({ max });
              }
            }}
            onClick={(e: any) => e.stopPropagation()}
            value={lineQuantities[id]}
          />
        ) : null;
      },
      renderHeader: () => (
        <RenderHeader
          col={{
            name: translate('VendorDeals.DiscountLineQuantityName'),
          }}
        />
      ),
      tableCellProps: { width: 150 },
    };
  }
  return {
    columns,
    errors,
    lineQuantities,
    selected: showQuantityCol ? selected : selectedRows,
    setUpAllSelected,
  };
}

export function useDiscountLineColumns(
  columnGroups: ColumnGroups[],
  importreferenceid: number,
  isXcmVendor: boolean,
  vendorProgramId: number,
  baseSalesValues?: Record<number, boolean>,
  setBaseSalesValues?: (v: Record<number, boolean>) => void,
  proficiencyValues?: Record<number, boolean>,
  setProficiencyValues?: (v: Record<number, boolean>) => void,
  showIncentivesHeader?: boolean,
  version?: number,
  enableRulesEngine?: boolean,
  productLocalizationForWeb?: string
): ColumnDef<LineDetails>[] {
  if (!showIncentivesHeader) {
    columnGroups = columnGroups.map((grp) => ({
      ...grp,
      columns: grp.columns.filter(
        (c) => c.field !== 'btl_base_sales' && c.field !== 'btl_proficiency'
      ),
    }));
  }
  const translate = useTranslate();
  const formatDate = useDateTimeFormatter();
  const [openPopup, setOpenPopup] = useState<{
    lineDetails: LineDetails;
    type: OpenPopupType;
  } | null>(null);

  const baseSalesCol = (cellValue: any, item: any) => {
    return (
      <BaseSalesToggle
        initialValue={cellValue}
        rowId={item.row.item_id}
        setValues={setBaseSalesValues}
        values={baseSalesValues}
      />
    );
  };

  const proficiencyCol = (cellValue: any, item: any) => {
    return (
      <BaseSalesToggle
        initialValue={cellValue}
        rowId={item.row.item_id}
        setValues={setProficiencyValues}
        values={proficiencyValues}
      />
    );
  };

  return columnGroups.flatMap((colGrp) =>
    colGrp.columns.map((col, index) => {
      const width =
        col.type === 'datetime' ? col.name.length * 8 : colGrp.name.length * 10;
      return {
        field: col.field,
        renderCell: (item) => {
          const cellValue =
            col.type === 'datetime' ? formatDate(item.value) : item.value;
          if (col.field === 'vpn' || col.field === 'product_group') {
            return (
              <ImSkuTableCol
                cellValue={cellValue}
                isXcmVendor={isXcmVendor}
                openPopup={openPopup}
                row={item.row}
                setOpenPopup={setOpenPopup}
                vendorProgramId={vendorProgramId}
              />
            );
          } else if (col.field === 'dd_tracking') {
            return <Tag label={cellValue} />;
          } else if (col.field === 'is_product_member_exclude') {
            return (
              <VpnHistoryCol
                cellValue={cellValue}
                importreferenceid={importreferenceid}
                openPopup={openPopup}
                row={item.row}
                setOpenPopup={setOpenPopup}
              />
            );
          } else if (col.field === 'btl_base_sales') {
            return item.row.showIncentives
              ? baseSalesCol(cellValue, item)
              : null;
          } else if (col.field === 'btl_proficiency') {
            return item.row.showIncentives
              ? proficiencyCol(cellValue, item)
              : null;
          } else if (col.field === 'dd_used_qty') {
            return (
              <OrderHistoryCol
                cellValue={cellValue}
                importreferenceid={importreferenceid}
                openPopup={openPopup}
                row={item.row}
                setOpenPopup={setOpenPopup}
              />
            );
          } else if (col.field === 'vd_customer_branch_number') {
            return item.value ? (
              <Tooltip title={item.value?.join(', ')}>
                <span>
                  {item.value
                    ?.slice(0, 3)
                    .join(', ')
                    .concat(item.value?.length > 3 ? '...' : '')}
                </span>
              </Tooltip>
            ) : (
              <></>
            );
          } else if (col.field === 'product_business_type') {
            return (
              <ProductBusinessTypeCol
                openPopup={openPopup}
                row={item.row}
                setOpenPopup={setOpenPopup}
              />
            );
          } else if (col.field === 'im_sku') {
            return (
              <ImSkuCol
                cellValue={item.value}
                enableRulesEngine={enableRulesEngine ?? false}
                isXcmVendor={isXcmVendor ?? false}
                openPopup={openPopup}
                productLocalizationForWeb={productLocalizationForWeb ?? ''}
                row={item.row}
                setOpenPopup={setOpenPopup}
                vendorProgramId={vendorProgramId}
                version={version ?? 0}
              />
            );
          } else {
            return cellValue;
          }
        },
        renderHeader: () => (
          <RenderHeader
            col={{
              ...col,
              name: translate(
                `VendorDeals.DiscountLine${upperFirst(col.field)}Name`
              ),
            }}
            colGrp={colGrp}
            index={index}
          />
        ),
        tableCellProps: { width },
      };
    })
  );
}

export const useCollasableRows = (rows: LineDetails[]) => {
  const [result, setResult] = useState<LineDetails[]>([]);

  useEffect(() => {
    setResult(rows);
  }, [rows]);

  const onExpand = useCallback(
    (ids: number[]) => {
      const newRows = [...rows];
      ids.forEach((id) => {
        const parentIndex = newRows?.findIndex((row) => row.item_id === id);
        const selectedRow = newRows?.[parentIndex];

        if (selectedRow?.childDiscountLines) {
          const children = selectedRow.childDiscountLines;
          newRows?.splice(parentIndex + 1, 0, ...children);
        }
      });
      setResult(newRows);
    },
    [rows]
  );
  return { onExpand, rows: result };
};

export function usePinnedColumns(
  columns: ColumnDef<LineDetails>[],
  pinnedColumns: { left?: string[] },
  enable?: boolean,
  border?: boolean
) {
  enable = enable ?? true;
  const handleScrollHorizontal = () => {
    if (enable && document !== null) {
      const columnHeaders = Array.from(
        document.querySelectorAll('.MuiTableCell-head')
      ).filter(
        (header) =>
          !pinnedColumns.left?.includes(header.getAttribute('data-field') ?? '')
      );
      const currentScrollPos = document.getElementById(
        'gds-vp-dd-table-body-container'
      )?.scrollLeft;

      columnHeaders.forEach((columnHeader) => {
        (
          columnHeader as HTMLElement
        ).style.transform = `translate3d(-${currentScrollPos}px, 0px, 0px)`;
      });
    }
  };

  const handleScrollRef = useRef(handleScrollHorizontal);
  handleScrollRef.current = handleScrollHorizontal;

  useEffect(() => {
    if (!enable) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let virtualScrollerElement: HTMLElement | null = null;
    const onScroll = () => handleScrollRef.current();

    const findVirtualScroller = () => {
      virtualScrollerElement = document.getElementById(
        'gds-vp-dd-table-body-container'
      );
      if (!virtualScrollerElement) {
        timeoutId = setTimeout(findVirtualScroller, 100);
      } else {
        virtualScrollerElement.addEventListener('scroll', onScroll);
        onScroll();
      }
    };
    findVirtualScroller();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      virtualScrollerElement?.removeEventListener('scroll', onScroll);
    };
  }, [enable]);

  const style: SxProps<Theme> = {
    '.MuiTableRow-root.Mui-selected': {
      '.MuiTableCell-body': { background: 'rgb(235 244 252)' },
    },
    '.MuiTableRow-root:hover': {
      '.MuiTableCell-body': { background: 'rgb(230, 242, 252)' },
    },
  };
  const borderStyle = border ? '1px solid #d1d1d1' : undefined;

  const checkboxStyles = (
    checkboxSelection: boolean,
    side: string,
    _columns: string[],
    collasibleField: boolean
  ) => {
    if (checkboxSelection) {
      style[`& .MuiTableCell-head[data-field="__check__"]`] = {
        background: '#F8F8F8;',
        height: 'inherit',
        position: 'sticky',
        zIndex: 1,
        ...(side === 'left' && {
          borderRight: _columns.length === 1 ? borderStyle : undefined,
          left: 0,
        }),
      };
      style[`& .MuiTableCell-body[data-field="__check__"]`] = {
        background: '#FFFF',
        ...(side === 'left' && {
          borderRight: _columns.length === 1 ? borderStyle : undefined,
          left: collasibleField ? 50 : 0,
        }),
        position: 'sticky',
        zIndex: 1,
      };
    }
  };

  const collasibleStyles = (side: string, _columns: string[]) => {
    style[`& .MuiTableCell-head[data-field="__COLLAPSABLE__"]`] = {
      background: '#F8F8F8;',
      height: 'inherit',
      position: 'sticky',
      zIndex: 1,
      ...(side === 'left' && {
        borderRight: _columns.length === 1 ? borderStyle : undefined,
        left: 0,
      }),
    };
    style[`& .MuiTableCell-body[data-field="__COLLAPSABLE__"]`] = {
      background: '#FFFF',
      ...(side === 'left' && {
        borderRight: _columns.length === 1 ? borderStyle : undefined,
        left: 0,
      }),
      position: 'sticky',
      zIndex: 1,
    };
  };

  const columnStyles = (
    _columns: string[],
    i: number,
    side: string,
    length: number,
    position: number
  ) => {
    style[`& .MuiTableCell-body[data-field="${_columns[i]}"]`] = {
      background: '#FFFF',
      ...(side === 'left' && {
        borderRight: i === length - 1 ? borderStyle : undefined,
        left: position,
      }),
      position: 'sticky',
      zIndex: 1,
    };
    style[`& .MuiTableCell-head[data-field="${_columns[i]}"]`] = {
      background: '#F8F8F8;',
      position: 'sticky',
      zIndex: 1,
      ...(side === 'left' && {
        borderRight: i === length - 1 ? borderStyle : undefined,
      }),
    };
  };

  return useMemo(() => {
    if (!enable) return {};

    const applyPinnedColumns = (side: 'left' | 'right', _columns: string[]) => {
      const checkboxSelection = _columns.includes(
        GRID_CHECKBOX_SELECTION_COL_DEF.field
      );
      const collasibleField = _columns.includes('__COLLAPSABLE__');
      let position = checkboxSelection ? 50 : 0;
      const length = _columns.length;
      for (let i = 0; i < length; i++) {
        const pinnedColumn = columns?.find(
          (c) => c.field === _columns?.[i - 1]
        );
        if (Number(pinnedColumn?.tableCellProps?.width)) {
          position += Number(pinnedColumn?.tableCellProps?.width);
        }
        columnStyles(_columns, i, side, length, position);
      }
      collasibleField && collasibleStyles(side, _columns);
      checkboxStyles(checkboxSelection, side, _columns, collasibleField);
    };
    if (pinnedColumns.left) applyPinnedColumns('left', pinnedColumns.left);
    return style;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, enable, pinnedColumns, style]);
}
